/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Michael Diponio
 * Created: 26/04/2022
 */

import * as React from 'react';
import Page from '@/wpt-lib/pdf/common/Page';
import { Row, Table, ValueCell } from '@/wpt-lib/pdf/common/Table';
import Heading from '@/wpt-lib/pdf/common/Heading';
import useData, { useGoals } from '@/wpt-lib/pdf/useData';
import { GoalType } from '@/wpt-lib/interfaces/PersonalGoal';
import Career from '@/wpt-lib/pdf/goals/Career';
import Equity from '@/wpt-lib/pdf/goals/Equity';
import MidLongTerm from '@/wpt-lib/pdf/goals/MidLongTerm';
import CareerProgression from '@/wpt-lib/pdf/goals/CareerProgression';
import ProgressUpdates from '@/wpt-lib/pdf/ProgressUpdates';
import Comments from '@/wpt-lib/pdf/Comments';
import CapabilityDevelopment from '@/wpt-lib/pdf/goals/CapabilityDevelopment';
import ResearchAndScholarship from '@/wpt-lib/pdf/goals/ResearchAndScholarship';
import ServiceAndEngagement from '@/wpt-lib/pdf/goals/ServiceAndEngagement';
import Leadership from '@/wpt-lib/pdf/goals/Leadership';
import TeachingAndEducation from '@/wpt-lib/pdf/goals/TeachingAndEducation';
import { getFormTypeKeys } from '@/wpt-lib/utils/formData';
import SectionComments from '@/wpt-lib/pdf/SectionComments';
import { WorkplanCommentSection } from '@/wpt-lib/interfaces/WorkplanComment';
import SubHeading from '@/wpt-lib/pdf/common/SubHeading';
import General from '@/wpt-lib/pdf/goals/General';

const GoalTitles: { [type in GoalType]: string } = {
    [GoalType.CAREER_GOAL]: 'Career Goals',
    [GoalType.MID_LONG_TERM_GOAL]: 'Medium–Long Term Goals',
    [GoalType.CAREER_PROGRESSION]: 'Career Progression',
    [GoalType.EQUITY_CONSIDERATION]: 'Equity Consideration',
    [GoalType.CAPABILITY_DEVELOPMENT]: 'Capability Development',
    [GoalType.TEACHING_AND_EDUCATIONAL_FACILITATION]: 'Teaching & Educational Facilitation',
    [GoalType.RESEARCH_AND_SCHOLARSHIP]: 'Research and Scholarship',
    [GoalType.SERVICE_AND_ENGAGEMENT]: 'Service and Engagement',
    [GoalType.LEADERSHIP]: 'Leadership',
    [GoalType.GENERAL]: 'General (Recommended for Professional And Senior Staff)',
};

// eslint-disable-next-line
const GoalComponents: { [type in GoalType]: React.ComponentType<any> } = {
    [GoalType.CAREER_GOAL]: Career,
    [GoalType.MID_LONG_TERM_GOAL]: MidLongTerm,
    [GoalType.CAREER_PROGRESSION]: CareerProgression,
    [GoalType.EQUITY_CONSIDERATION]: Equity,
    [GoalType.CAPABILITY_DEVELOPMENT]: CapabilityDevelopment,
    [GoalType.TEACHING_AND_EDUCATIONAL_FACILITATION]: TeachingAndEducation,
    [GoalType.RESEARCH_AND_SCHOLARSHIP]: ResearchAndScholarship,
    [GoalType.SERVICE_AND_ENGAGEMENT]: ServiceAndEngagement,
    [GoalType.LEADERSHIP]: Leadership,
    [GoalType.GENERAL]: General,
};

const Goals = () => {
    const goals = useGoals().sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    const comments = useData().comments.filter((c) => c.section === WorkplanCommentSection.PERSONAL_GOAL);

    return (
        <>
            {goals.map((goal, i) => (
                <Page key={i}>
                    {i === 0 && <Heading text="Goals" />}
                    <Table cols={2}>
                        <Row>
                            <ValueCell
                                label={`Goal ${i + 1}`}
                                value={'' + goal.data.goalName}
                                fontSize={12}
                                bg="#565656"
                                color="white"
                            />
                        </Row>
                        {goal.data.goalPlan && (
                            <Row>
                                <ValueCell
                                    label="How do you intend to achieve this goal?"
                                    value={'' + goal.data.goalPlan}
                                />
                            </Row>
                        )}
                        {getFormTypeKeys(goal.data?.goalType).map((goalType, j) => (
                            <React.Fragment key={j}>
                                <Row>
                                    <SubHeading level={1} text={GoalTitles[goalType as GoalType]} />
                                </Row>

                                {goalType in GoalComponents &&
                                    React.createElement(GoalComponents[goalType as GoalType], {
                                        index: i + 1,
                                        ...goal.data,
                                    })}
                            </React.Fragment>
                        ))}
                        <ProgressUpdates item={goal.workplanItem} />
                        <Comments comments={goal.workplanItem.comments} />
                    </Table>
                </Page>
            ))}
            {comments.length > 0 && (
                <Page>
                    <SectionComments heading="Goal Comments" comments={comments} />
                </Page>
            )}
        </>
    );
};

export default Goals;
